import { mag } from './rechten.js';
/**
 * De regels rond personen en begeleiding die client en server allebei moeten
 * kennen, zodat het scherm dezelfde uitkomst geeft als de server. De server
 * blijft de plek waar het wordt afgedwongen; dit is er zodat de knop al uit
 * staat vóór iemand hem indrukt, met dezelfde woorden erbij.
 */
const WEEK_MS = 7 * 24 * 60 * 60 * 1000;
/**
 * Mag deze persoon de gegevens van die rij wijzigen?
 *
 * Wie de personen beheert mag iedereen: de Admin en het Core Team. Een
 * buddy mag zichzelf en de mensen die hij begeleidt, en dat is dezelfde grens
 * als bij zijn gesprekken: kijken mag breed, wijzigen gaat over zijn eigen
 * rijen.
 *
 * Wat hier NIET onder valt: vinkjes zetten, uitnodigen, uitzetten en
 * archiveren. Die blijven bij `persoon:manage`, ook op de eigen rij. Dit gaat
 * over naam, niveau en opdracht.
 */
export function magPersoonWijzigen(rechten, rij, huidigeId) {
    if (mag(rechten, 'persoon:manage'))
        return true;
    if (!mag(rechten, 'begeleiding:manage:own'))
        return false;
    return rij.id === huidigeId || rij.buddy?.id === huidigeId;
}
/**
 * Wie er toegang uitdeelt, deelt dat vinkje niet uit. Wie een Core Team-lid kan
 * aanwijzen, kan er ook zichzelf een tweede van maken en daarmee de kring
 * uitbreiden buiten het zicht van de Admin om; wie het Admin-vinkje kan
 * zetten geeft zichzelf de instellingen en de hulpvragenlijst. Allebei aan de
 * Admin dus.
 */
const ALLEEN_ADMIN = ['coreTeam', 'admin'];
/**
 * Mag deze persoon dat vinkje zetten?
 *
 * Vinkjes zetten hangt aan `persoon:manage`, en dat recht heeft het Core Team
 * ook. Zonder deze regel maakt een Core Team-lid zichzelf Admin, en dan zijn de
 * instellingen en de hulpvragenlijst alsnog open: het onderscheid dat het
 * Core Team-vinkje maakt zou decoratie zijn.
 *
 * Een Core Team-lid zet dus het buddyvinkje en het View Only-vinkje, bij iedereen.
 * Wie er bij komt in het beheer van de app blijft een keuze van de Admin.
 * Dat geldt aan én uit: de vinkjes van de Admins afnemen is hetzelfde lek
 * van de andere kant.
 *
 * Dit staat los van de regel dat je je éigen Admin-vinkje niet zet. Die gaat
 * over de rij en niet over het vinkje, en wordt afgedwongen in
 * `personen.service.ts`.
 */
export function magVinkjeZetten(rechten, soort) {
    if (!mag(rechten, 'persoon:manage'))
        return false;
    return ALLEEN_ADMIN.includes(soort) ? Boolean(rechten?.admin) : true;
}
/**
 * Mag deze persoon het e-mailadres van die rij zetten?
 *
 * Een leeg adres vullen is administratie. Dat mag iedereen die de rij mag
 * wijzigen, dus ook een buddy bij zichzelf en bij wie hij begeleidt. Het kan
 * geen inlog raken, want zonder adres bestaat er geen inlog: het adres ís de
 * inlognaam.
 *
 * Een gevuld adres wijzigen of leeghalen is iets anders. Dat verandert waarmee
 * iemand inlogt, dus dat is van de Admin. Ook bij iemand die nog geen
 * account heeft, want dan hangt er een uitnodiging aan het oude adres.
 */
export function magAdresZetten(rechten, rij, huidigeId) {
    if (!magPersoonWijzigen(rechten, rij, huidigeId))
        return false;
    return rij.email === null || Boolean(rechten?.admin);
}
/**
 * Waarom deze twee niet aan elkaar kunnen, of `null` als het mag.
 *
 * Wie begeleidt moet het buddyvinkje hebben, want begeleiden betekent het
 * gesprek opnemen en aanleveren. Wie begeleid wordt kan iedereen zijn, ook een
 * andere buddy: een medior is buddy van een junior en wordt zelf gecoacht door
 * een senior. Dat is een ketting en geen twee lagen.
 *
 * Het vinkje en niet "kan inloggen": een Admin, een Core Team-lid en wie View Only
 * heeft kunnen inloggen maar leveren geen gesprekken aan. Die horen dus geen
 * gecoachten te kunnen krijgen.
 *
 * Met `keten` erbij komt de derde voorwaarde: de koppeling mag geen rondje
 * maken. Zonder die lijst blijft die controle achterwege.
 *
 * De opdracht is bewust géén voorwaarde meer. De voorkeur is dat coach en
 * gecoachte op dezelfde opdracht zitten, maar in de praktijk lukt dat vaak niet,
 * en een voorkeur die je als regel afdwingt is een blokkade. De opdracht blijft
 * wel op de persoon staan: daarmee is te zien waar iemand zit en of een bepaalde
 * plek een bepaald soort hulpvraag oplevert.
 */
export function redenGeenKoppeling(buddy, gecoachte, keten) {
    if (buddy.id === gecoachte.id)
        return 'Iemand kan niet zijn eigen buddy zijn.';
    if (!buddy.rechten.buddy) {
        return `${buddy.naam} is geen buddy en kan dus geen gesprekken aanleveren.`;
    }
    if (keten && maaktRondje(keten, gecoachte.id, buddy.id)) {
        return `${buddy.naam} hangt zelf al onder ${gecoachte.naam}. Dan gaat de begeleiding in een rondje.`;
    }
    return null;
}
/** Een keten is in de praktijk drie stappen diep. Deze grens is er alleen zodat
 *  een rondje in de gegevens geen oneindige lus wordt. */
const KETEN_MAX = 100;
/**
 * Zou deze koppeling een rondje maken?
 *
 * Begeleiding is een ketting: een medior hangt onder een senior en begeleidt
 * zelf een junior. Daarmee ontstaat de vraag of iemand niet onder zichzelf kan
 * komen te hangen. We lopen vanaf de nieuwe buddy omhoog en kijken of we bij de
 * gecoachte uitkomen.
 */
export function maaktRondje(keten, gecoachteId, buddyId) {
    let huidig = buddyId;
    for (let stap = 0; huidig !== undefined && stap < KETEN_MAX; stap += 1) {
        if (huidig === gecoachteId)
            return true;
        huidig = keten.get(huidig);
    }
    return false;
}
/** Het ijkpunt: het laatste gesprek, en anders het moment van toevoegen. */
export function peilmoment({ laatsteGesprek, toegevoegdOp }) {
    return laatsteGesprek ?? toegevoegdOp;
}
/** Hele weken tussen twee momenten. Negatief wordt 0: een datum in de toekomst
 *  is geen achterstand maar een typefout, en die hoort niet bovenaan te komen. */
export function wekenSinds(datum, nu) {
    const verschil = new Date(nu).getTime() - new Date(datum).getTime();
    if (!Number.isFinite(verschil) || verschil < 0)
        return 0;
    return Math.floor(verschil / WEEK_MS);
}
/** Staat deze persoon te lang stil? De drempel is instelbaar; zie
 *  `gesprekDrempelWeken` in `instellingen.ts`. */
export function isAchterstallig(peiling, drempelWeken, nu) {
    return wekenSinds(peilmoment(peiling), nu) >= drempelWeken;
}
/**
 * Wie het langst stilstaat bovenaan.
 *
 * Sorteert op het peilmoment en niet op "achterstallig ja/nee": binnen de groep
 * die eroverheen is wil de Admin de ergste eerst zien, en de rest van de
 * lijst blijft dan in dezelfde volgorde doorlopen in plaats van in twee blokken
 * uiteen te vallen.
 */
export function sorteerOpStilte(rijen) {
    return [...rijen].sort((a, b) => peilmoment(a).localeCompare(peilmoment(b)));
}
/**
 * Van wie alles mag naar wie niet inlogt.
 *
 * De volgorde volgt hoe ver iemand in de app komt: eerst wie beheert, dan wie
 * de administratie bijhoudt, dan wie gesprekken aanlevert, dan wie alleen
 * meekijkt, dan wie alleen in de administratie staat. Wie er het buddyvinkje bij
 * heeft staat boven wie dat niet heeft. Binnen een rang op naam.
 */
function rang({ buddy, coreTeam, admin, viewOnly }) {
    if (admin)
        return buddy ? 0 : 1;
    if (coreTeam)
        return buddy ? 2 : 3;
    if (buddy)
        return 4;
    return viewOnly ? 5 : 6;
}
export function sorteerOpRechten(rijen) {
    return [...rijen].sort((a, b) => rang(a.rechten) - rang(b.rechten) || a.naam.localeCompare(b.naam, 'nl'));
}
/**
 * De opdrachten van iemand in één regel, voor achter een naam.
 *
 * Iemand zit op één tot drie opdrachten en een lijstje van drie namen achter een
 * naam maakt de regel onleesbaar. Vanaf twee dus de eerste plus een teller; de
 * volledige lijst staat op zijn eigen pagina.
 */
export function opdrachtenTekst(opdrachten, leeg = 'geen opdracht') {
    const [eerste, ...rest] = opdrachten;
    if (!eerste)
        return leeg;
    return rest.length === 0 ? eerste.naam : `${eerste.naam} +${rest.length}`;
}
